import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { otherGroupsRegistrationFormSchema } from '../components/registrationFormComponents/schema';

import { AppContext } from '../contexts/app.context';
import { Button, Loader, Text } from '../components';
import {
  EstablishmentDetailsForm,
  ClassDetailsForm,
  EmailConsentForm,
  InchargePersonForm,
  TotalStudentDisplay,
} from '../components/registrationFormComponents';
import { otherGroupsFormEstablishmentTypes } from '../components/registrationFormComponents/data';
import { otherGroupsRegistration, updateDashboardDetails } from '../service';
import { toast } from 'react-toastify';
import { scrollToClass, submitHelper } from '../components/registrationFormComponents/util';

const translationPrefix = 'otherGroupsRegistrationForm.form';

const classInitialValues = {
  name: '',
  level: '',
  numberOfStudents: '',
  sector: '',
  visitDay: '',
  arrivalTime: '',
  departureTime: '',
  modeOfTransport: '',
  otherModeOfTransport: '',
  picnicOnSite: 'false',
  handicap: 'false',
  handicapDescription: '',
  responsibleInformation: {
    firstName: '',
    lastName: '',
    function: '',
    email: '',
    phone: '',
  },
};

const OtherGroupsRegistrationForm = ({ storedData, refresh, refreshing }) => {
  const { t } = useTranslation();
  const { setBaseContainerConfig } = useContext(AppContext);
  const [otherGroupType, setOtherGroupType] = useState('other-school');
  const [saving, setSaving] = useState(false);
  const [openClassIndex, setOpenClassIndex] = useState(null);

  const formik = useFormik({
    initialValues: {
      establishmentDetails: {},
      inchargePersonDetails: {},
      inchargePersonAnother: 'false',
      classes: [
        {
          ...classInitialValues,
        },
      ],
      emailConsent: {
        emailConsent: 'true',
        email: '',
      },
    },
    validationSchema: otherGroupsRegistrationFormSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      try {
        const emailConsentGiven = values?.emailConsent?.emailConsent;
        const emailConsentEmail = values?.emailConsent?.email;
        const inchargePersonEmail = values?.inchargePersonDetails?.email;

        if (emailConsentGiven === 'true' && emailConsentEmail !== inchargePersonEmail) {
          toast.error(t('otherGroupsRegistrationForm.popupError.emailConsentEmail'));
          return;
        }

        setSaving(true);
        !storedData
          ? await otherGroupsRegistration(values)
          : await updateDashboardDetails({ ...values, formType: 'other-group' });
        toast.success(t('otherGroupsRegistrationForm.success'));

        if (refresh) {
          refresh();
        }
      } catch (error) {
        console.log(error);
        if (error?.response?.data?.errors) {
          const errors = Object.keys(error?.response?.data?.errors);
          errors.forEach((error) => {
            toast.error(t(`${translationPrefix}.popupError.${error}`));
          });
        } else {
          toast.error(t('otherGroupsRegistrationForm.error'));
        }
      } finally {
        setSaving(false);
      }
    },
  });

  useEffect(() => {
    if (
      [...otherGroupsFormEstablishmentTypes]
        .slice(0, 2)
        .map((type) => type.value)
        .includes(formik.values.establishmentDetails.type)
    ) {
      setOtherGroupType('other-school');
    } else {
      setOtherGroupType('other-group');
    }
  }, [formik.values.establishmentDetails.type]);

  useEffect(() => {
    if (!storedData) return;
    formik.setValues(storedData);
  }, [storedData]);

  useEffect(() => {
    setBaseContainerConfig({
      heading: t('otherGroupsRegistrationForm.header.heading'),
      subHeading: t('otherGroupsRegistrationForm.header.subHeading'),
      buttonText: t('loginButtonText'),
      buttonURL: '/login',
    });
  }, []);

  return (
    <>
      <div className="text-center">
        <Text type={'big'} text={t('otherGroupsRegistrationForm.heading')} />
      </div>

      <form
        onSubmit={(e) => {
          submitHelper(e, formik, t);
        }}>
        <div className="w-full mt-12">
          <Text
            type={'small'}
            text={t(`${translationPrefix}.heading`)}
            className={'bg-blue-10 text-center text-white p-2 w-full rounded-md mb-2'}
          />

          <div className="bg-lightBlue-10 rounded-lg p-4 sm:px-10 pt-10">
            <EstablishmentDetailsForm
              type={'other-group'}
              formik={formik}
              translationPrefix={translationPrefix}
            />

            <span className="block my-10 bg-black h-[0.5px] w-full" />

            <Text
              type={'small'}
              text={t(`${translationPrefix}.inchargePerson.heading`)}
              className={'text-center mb-6 font-semibold'}
            />

            <InchargePersonForm formik={formik} translationPrefix={translationPrefix} />

            <span className="block my-10 bg-black h-[0.5px] w-full" />
          </div>
        </div>

        <div className="w-full mt-6">
          <Text
            type={'small'}
            text={t(`${translationPrefix}.visit.heading`)}
            className={'bg-blue-10 text-center text-white p-2 w-full rounded-md mb-2'}
          />

          <div className="bg-lightBlue-10 rounded-lg p-4 sm:px-10 pt-10">
            <ClassDetailsForm
              type={otherGroupType}
              formik={formik}
              translationPrefix={translationPrefix}
              openClassIndex={openClassIndex}
              experienceInteractionConfig={{ editable: true }}
            />

            <div className="w-full flex items-center justify-center my-8">
              <Button
                type="button"
                text={t('otherGroupsRegistrationForm.addClassButtonText')}
                className={'bg-transparent border-2 border-red-10 !text-red-10 rounded-md'}
                callback={() => {
                  formik.setFieldValue('classes', [
                    ...formik.values.classes,
                    { ...classInitialValues },
                  ]);
                  setOpenClassIndex(formik.values.classes.length);
                  scrollToClass(`class-${formik.values.classes.length}`);
                }}
              />
            </div>
            <TotalStudentDisplay formik={formik} translationPrefix={translationPrefix} />
          </div>
        </div>

        <div className="w-full mt-6">
          <Text
            type={'medium'}
            text={t(`${translationPrefix}.emailConsent.heading`)}
            className={'mb-6 mt-4'}
          />

          <EmailConsentForm formik={formik} translationPrefix={translationPrefix} />

          <Button
            type="submit"
            text={t('otherGroupsRegistrationForm.submitButtonText')}
            className={'mt-20'}
            loading={formik.isSubmitting}
          />

          <Text
            type={'medium'}
            text={t(`${translationPrefix}.support.heading`)}
            className={'mt-12'}
          />
          <Text
            type={'small'}
            text={t(`${translationPrefix}.support.paragraph`)}
            className={'mb-2'}
          />
          <Text
            type={'small'}
            text={t(`${translationPrefix}.termsAndConditions`)}
            className={'mt-6 text-red-10'}
          />
        </div>
      </form>

      {(saving || refreshing) && (
        <Loader
          bgColor={'transparent'}
          loaderType={'sync'}
          text={storedData ? t('updating') : t('saving')}
        />
      )}
    </>
  );
};

export default OtherGroupsRegistrationForm;
